import { z } from 'zod';
import { objectId } from './schemas.js';
/**
 * Micro-learning: short lessons written from what people are discussing on
 * social media this week.
 *
 * The workspace owner names the topics the team should keep up with, and each
 * person may add a few of their own. Every day the server asks Composio for the
 * most talked-about posts on those topics, has the assistant boil them down to a
 * two-minute card, and keeps the result as a feed. A workspace may also name a
 * channel, and the day's first lesson is posted there so it can be discussed in
 * its thread.
 *
 * Composio runs on one platform key. Hacker News and LinkedIn need no account
 * of their own — LinkedIn is reached through Composio's web search, since
 * LinkedIn's own API does not search posts — while Reddit, X and YouTube read
 * through accounts connected once, under the platform's Composio user.
 */
/* ------------------------------- sources -------------------------------- */
export const LEARNING_SOURCES = ['hackernews', 'reddit', 'twitter', 'linkedin', 'youtube'];
export const LEARNING_SOURCE_LABELS = {
    hackernews: 'Hacker News',
    reddit: 'Reddit',
    twitter: 'X',
    linkedin: 'LinkedIn',
    youtube: 'YouTube',
};
/* ------------------------------- limits --------------------------------- */
export const LEARNING_MAX_TOPICS = 10;
export const LEARNING_MAX_INTERESTS = 5;
export const LEARNING_TOPIC_MAX_LENGTH = 60;
export const LEARNING_MAX_LESSONS_PER_DAY = 5;
export const LEARNING_DEFAULT_LESSONS_PER_DAY = 3;
export const LEARNING_DEFAULT_TIME = '09:00';
export const LEARNING_FEED_PAGE_SIZE = 20;
export const DEFAULT_LEARNING_SETTINGS = {
    topics: [],
    sources: ['hackernews', 'reddit'],
    lessonsPerDay: LEARNING_DEFAULT_LESSONS_PER_DAY,
    time: LEARNING_DEFAULT_TIME,
    channelId: null,
};
/* ------------------------------- schemas -------------------------------- */
const topic = z.string().trim().min(2).max(LEARNING_TOPIC_MAX_LENGTH);
/** Case-insensitive de-duplication, keeping the first spelling. */
const uniqueTopics = (topics) => topics.filter((value, index) => topics.findIndex((other) => other.toLowerCase() === value.toLowerCase()) === index);
export const updateLearningSettingsSchema = z
    .object({
    topics: z.array(topic).max(LEARNING_MAX_TOPICS).transform(uniqueTopics),
    sources: z
        .array(z.enum(LEARNING_SOURCES))
        .min(1, 'Pick at least one source')
        .max(LEARNING_SOURCES.length)
        .transform((sources) => [...new Set(sources)]),
    lessonsPerDay: z.number().int().min(1).max(LEARNING_MAX_LESSONS_PER_DAY),
    time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:mm'),
    channelId: objectId.nullable(),
})
    .partial()
    .refine((body) => Object.keys(body).length > 0, 'At least one field is required');
export const updateLearningInterestsSchema = z.object({
    interests: z.array(topic).max(LEARNING_MAX_INTERESTS).transform(uniqueTopics),
});
export const learningFeedQuerySchema = z.object({
    cursor: objectId.optional(),
    limit: z.coerce.number().int().min(1).max(50).default(LEARNING_FEED_PAGE_SIZE),
});
